// Founder Activation panel — Slice 2. One-click activation for the
// founder, gated behind <AdminOnly> AND the server-side
// is_strikza_admin() + 4-condition guards (the UI can bypass
// nothing). Honest by construction: the "Activate" button is
// enabled ONLY when the server reports `activatable` (real verified
// web corpus >= corpus_min). Every RPC result/error is shown
// verbatim — no fabricated success.

import { useState } from "react";
import { useActivationOverview } from "../../sportCoverage/useActivationOverview.ts";

const card: React.CSSProperties = {
  border: "1px solid var(--sa-hairline, #2a2a30)",
  borderRadius: 6,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
const eyebrow: React.CSSProperties = {
  fontFamily: "var(--sa-font-mono, monospace)",
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--sa-fg-muted, #9a9aa2)",
};
const row: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.4fr 1fr 1fr auto",
  gap: 10,
  alignItems: "center",
  padding: "8px 0",
  borderBottom: "1px solid var(--sa-hairline, #2a2a30)",
  fontFamily: "var(--sa-font-mono, monospace)",
  fontSize: 12,
};

export function ActivationPanelPage(): JSX.Element {
  const {
    footballDark,
    sports,
    loading,
    error,
    busyLeague,
    activateFootballLeague,
  } = useActivationOverview();
  const [result, setResult] = useState<string | null>(null);

  async function onActivate(code: string): Promise<void> {
    setResult(null);
    const r = await activateFootballLeague(code);
    setResult(`${r.ok ? "✓" : "✗"} ${code}: ${r.message}`);
  }

  const ready = footballDark.filter((l) => l.activatable);
  const waiting = footballDark.filter((l) => !l.activatable);

  return (
    <div
      style={{ padding: "20px 24px", maxWidth: 1000, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}
      data-page="admin-activation"
    >
      <header style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>Activation</h1>
        <span style={eyebrow}>
          Founder-only. The server still enforces admin + the
          4-condition gate + real corpus on every action — this panel
          cannot bypass anything. A league is enabled only when its
          verified web-search corpus genuinely meets the minimum.
        </span>
      </header>

      {result ? (
        <div style={card} data-testid="activation-result">{result}</div>
      ) : null}
      {error ? (
        <div style={card} data-testid="activation-error">
          Overview unavailable: {error}
        </div>
      ) : null}

      {loading ? (
        <div style={card} data-testid="activation-loading">Loading…</div>
      ) : (
        <>
          <section style={card} data-testid="activation-ready">
            <span style={eyebrow}>
              Ready to activate · {ready.length}
            </span>
            {ready.length === 0 ? (
              <span style={{ color: "var(--sa-fg-muted)" }}>
                No football league has a sufficient verified corpus yet.
              </span>
            ) : (
              ready.map((l) => (
                <div key={l.league_code} style={row} data-testid={`ready-${l.league_code}`}>
                  <span>{l.league_name}</span>
                  <span>phase {l.phase}</span>
                  <span>
                    corpus {l.web_corpus}/{l.corpus_min}
                  </span>
                  <button
                    type="button"
                    data-testid={`activate-${l.league_code}`}
                    disabled={busyLeague === l.league_code}
                    onClick={() => void onActivate(l.league_code)}
                    style={{
                      padding: "6px 12px",
                      background: "transparent",
                      color: "var(--sa-accent-positive, #1fbf75)",
                      border: "1px solid var(--sa-accent-positive, #1fbf75)",
                      borderRadius: 3,
                      cursor: busyLeague ? "default" : "pointer",
                      fontFamily: "var(--sa-font-mono, monospace)",
                      fontSize: 10.5,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                    }}
                  >
                    {busyLeague === l.league_code ? "Activating…" : "Activate"}
                  </button>
                </div>
              ))
            )}
          </section>

          <section style={card} data-testid="activation-waiting">
            <span style={eyebrow}>
              Accruing corpus · {waiting.length} (not yet activatable)
            </span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {waiting.slice(0, 60).map((l) => (
                <span
                  key={l.league_code}
                  title={`web corpus ${l.web_corpus}/${l.corpus_min}`}
                  style={{
                    fontSize: 11,
                    padding: "2px 6px",
                    borderRadius: 3,
                    border: "1px solid var(--sa-hairline, #2a2a30)",
                    color: "var(--sa-fg-muted, #9a9aa2)",
                  }}
                >
                  {l.league_name} {l.web_corpus}/{l.corpus_min}
                </span>
              ))}
            </div>
          </section>

          <section style={card} data-testid="activation-sports">
            <span style={eyebrow}>Other sports — calibration required</span>
            <span style={{ fontSize: 11, color: "var(--sa-fg-muted)" }}>
              A non-football sport needs a real ≥4-week calibration
              sign-off before activation (see sports_activation_runbook.md).
              Status only — not one-click here on purpose.
            </span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sports.map((s) => (
                <span
                  key={s.sport_code}
                  data-testid={`sport-${s.sport_code}`}
                  style={{
                    fontSize: 11,
                    padding: "2px 6px",
                    borderRadius: 3,
                    border: `1px solid ${s.calibration_signed ? "var(--sa-accent-positive, #1fbf75)" : "var(--sa-hairline, #2a2a30)"}`,
                    color: s.calibration_signed
                      ? "var(--sa-accent-positive, #1fbf75)"
                      : "var(--sa-fg-muted, #9a9aa2)",
                  }}
                >
                  {s.display_name} · {s.status}
                  {s.calibration_signed ? " · calibrated" : " · uncalibrated"}
                </span>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
